// src/data/talkGuides.js
//
// TALK GUIDES: what a parent can say when their player is in a particular spot.
// Each guide is the same shape: when it applies, what to say first, what not to
// say, the one question worth asking, and when to get more help. Written for
// the parent of a youth athlete; nothing here is clinical. Sport words are
// placeholders ({sport}, {surface}, {venue}, {room}) filled from data/sports.js.
//
// They are the results screen of the parent check-in (ASSESSMENT_LIBRARY
// parent_v1): a low answer on an item opens the guide that fits it, through
// PARENT_ITEMS below. They are also offered as a library on the Parent Hub.
//
// Voice matches the Hockey Alberta content bank: plain, second person, short.

import { fillSport, fillSportDeep } from './sports';

export const TALK_GUIDES = [
    {
        id: 'car-ride-home',
        title: 'The car ride home after a loss',
        when: 'They played badly, or the team lost, and you are both in the car.',
        first: [
            '"I love watching you play." Then nothing. Silence is fine.',
            'Food and a drink before any conversation. Nobody debriefs well hungry.',
            'If they bring it up, listen. If they don\'t, leave it. The debrief can wait until they ask for it.'
        ],
        avoid: [
            'Replaying the mistakes, even gently. They have already replayed them.',
            '"If you had just..." Anything that starts that way.',
            'Praising effort in a voice that sounds like a consolation prize.'
        ],
        ask: 'What was one thing that felt good out there?',
        more: 'If every ride home is tense for a few weeks, that is a pattern, not a game. Worth a calm conversation on a non-{sport} day.'
    },
    {
        id: 'benched-or-cut',
        title: 'They got benched, cut, or their confidence dipped',
        when: 'Less ice time, a lower line, not making the team, or a stretch where they have stopped believing in themselves.',
        first: [
            'Let them feel it first. "That is a hard one" beats a plan.',
            'Remind them of the evidence: a thing you have watched them get better at this year.',
            'When they are ready, ask what they would want to work on, and let them lead.'
        ],
        avoid: [
            'Calling the coach that night. Sleep on it, and if you still want to talk, do it privately and calmly.',
            'Blaming the coach or the other players in front of them. It teaches them to look outward.',
            '"You are better than that kid." Comparison does not build confidence; evidence does.'
        ],
        ask: 'What is one thing you did better this month than you could in September?',
        more: 'If the dip lasts and starts showing up at school or with friends, the "gone quiet" guide applies.'
    },
    {
        id: 'before-tryouts',
        title: 'Before tryouts',
        when: 'The week before, the night before, the drive there.',
        first: [
            'Nerves mean it matters. Say that out loud: "Nervous means you are ready."',
            'Routine over pep talk: same breakfast, same warm-up, same bag packed the night before.',
            'Tell them you are proud whatever happens, before the result, not after.'
        ],
        avoid: [
            'Adding stakes: what it means for the season, for the family, for the drive.',
            '"Just relax." Nobody has ever relaxed on command.',
            'Talking about other kids trying out.'
        ],
        ask: 'What is your first-shift job tomorrow? (A job, not a result.)',
        more: 'If tryout nerves become not sleeping or not eating for days, that is worth a conversation with someone outside the family.'
    },
    {
        id: 'gone-quiet',
        title: 'They have gone quiet',
        when: 'Quieter than usual, not joking around, in their room more, short answers for more than a week or two.',
        first: [
            'Notice it out loud, lightly: "You have seemed a bit flat lately. I am around if you want to talk."',
            'Ask twice. The first "how are you?" gets "fine". The second, later, gets the real answer.',
            'Side by side beats face to face: the car, a walk, cooking. Eye contact makes it an interview.'
        ],
        avoid: [
            'An interrogation. One question, then space.',
            '"What is wrong with you?" Even kindly.',
            'Fixing it in the same conversation. Listening is the job for the first few rounds.'
        ],
        ask: 'Is it a {sport} thing, a school thing, a friends thing, or just a tired thing?',
        more: 'A rough week is normal. Two or three weeks of flat, withdrawn or not sleeping is a reason to talk to their doctor or a counsellor, and to let them know Kids Help Phone exists: text CONNECT to 686868.'
    },
    {
        id: 'wants-to-quit',
        title: 'They want to quit',
        when: '"I don\'t want to play anymore." In the car, after a bad week, or out of nowhere.',
        first: [
            'Do not decide anything in the car. "Okay. Let\'s talk about it properly this weekend."',
            'Find out what is underneath: not fun anymore, a friend thing, a coach thing, pressure, or just tired.',
            'Ask what they would miss. Sometimes the answer tells them more than it tells you.'
        ],
        avoid: [
            '"We paid for the season." True, and not the conversation.',
            '"You are not a quitter." Labels close the door.',
            'Agreeing instantly to end it, or refusing instantly. Both skip the part where you learn why.'
        ],
        ask: 'If you could change one thing about {sport} right now, what would it be?',
        more: 'If the "why" is a person or a situation at the {venue}, the "something an adult did" or "left out" guides come first.'
    },
    {
        id: 'left-out',
        title: 'A teammate is being picked on, or they are',
        when: '{Room} stuff, a group chat, being left out, name-calling, or they have told you about someone else.',
        first: [
            '"Thank you for telling me." First, every time.',
            'Get the plain facts without the heat: what happened, who was there, how often.',
            'Agree the next step together. For a one-off, maybe nothing yet. For a pattern, a private word with the coach.'
        ],
        avoid: [
            'Messaging the other parent that night.',
            '"Just ignore them." It tells them the problem is theirs to manage.',
            'Promising to keep it secret if someone is being hurt.'
        ],
        ask: 'Do you want help with this, or do you want me to know about it?',
        more: 'If it continues after the coach knows, the association is the next step. The Alberta Bullying Helpline is 310-1818, call or text, any time.'
    },
    {
        id: 'something-an-adult-did',
        title: 'They mention something a coach or adult did',
        when: 'Something that made them uncomfortable, unsafe, singled out, or that they are not sure was okay.',
        first: [
            '"You did the right thing telling me." Say it before anything else.',
            'Listen to the whole thing. Do not fill in gaps or react to each part.',
            'Write down what they said, in their words, the same day.'
        ],
        avoid: [
            'Investigating it yourself: confronting the adult, asking other kids.',
            'Minimizing: "I am sure they didn\'t mean it."',
            'Making them repeat it to several people.'
        ],
        ask: 'Do you feel okay going to the {venue} this week?',
        more: 'It is the adults\' job to work out where this goes, not theirs. Depending on what happened: the team\'s head coach or manager, the {sport} association, or Hockey Canada\'s Independent Third Party for serious maltreatment. If anyone is in danger now, 911.'
    },
    {
        id: 'sleep-and-phones',
        title: 'Sleep, phones and fuel',
        when: 'Tired at practice, flat in the mornings, the phone in the bedroom, skipping food on {sport} days.',
        first: [
            'Protect the wake time, not just the bedtime. Same wake-up seven days a week is the single biggest fix.',
            'Phone charges outside the bedroom. That is a house rule, and it applies to you too.',
            'Water bottle in the bag, snack packed before the game, something to eat within an hour of leaving the {venue}.'
        ],
        avoid: [
            'Making it a punishment. It is a performance plan.',
            'Changing five things at once. Pick the phone or the wake time, hold it for two weeks.',
            'Rules you do not follow yourself.'
        ],
        ask: 'Which one do you think would change your game the most: sleep, food, or the phone?',
        more: 'If they are sleeping a lot more or a lot less than usual for a few weeks, and nothing explains it, that is worth mentioning to their doctor.'
    },
    {
        id: 'says-something-scary',
        title: 'They say something that scares you',
        when: 'Anything about not wanting to be here, hurting themselves, or feeling hopeless. Even as a joke.',
        first: [
            'Stay calm on the outside and stay with them. "I am really glad you told me."',
            'Ask directly: "Are you thinking about hurting yourself?" Asking does not plant the idea; it opens the door.',
            'Get help tonight, not next week: their doctor, 9-8-8 (call or text), or the emergency department.'
        ],
        avoid: [
            '"Don\'t say things like that." It teaches them not to tell you.',
            'Leaving them alone if you are worried about tonight.',
            'Promising to keep it between you.'
        ],
        ask: 'Can we call someone together right now?',
        more: 'If there is immediate danger, 911. 9-8-8 is the Suicide Crisis Helpline, free, 24/7, by call or text. Kids Help Phone is 1-800-668-6868 or text CONNECT to 686868.'
    },
    {
        id: 'says-something-hurts',
        title: 'They say something hurts',
        when: 'A sore knee, a wrist, a headache after a hit, or a tweak they have been hiding.',
        first: [
            'Believe them. Hiding pain to stay in the lineup is common; telling you is the brave version.',
            'Sore or hurt? Sore is dull, both sides, fades with warm-up. Hurt is sharp, one spot, worse as they go.',
            'Any hit to the head gets checked, every time. No "shake it off".'
        ],
        avoid: [
            '"Toughen up." It guarantees you will not hear about the next one.',
            'Deciding they are fine because they want to play.',
            'Letting them play through sharp pain to avoid letting the team down.'
        ],
        ask: 'On a scale of 1 to 10, what is it right now?',
        more: 'Sharp pain, swelling, a limp, or any head knock means a professional looks at it before the next game. Health Link 811 can tell you where to go.'
    },
    {
        id: 'after-a-hit-to-the-head',
        title: 'After a hit to the head',
        when: 'A hit, a fall or a collision in {sport}, and they say they are fine. Or they say nothing and seem off.',
        first: [
            'Done for the day, no exceptions. Hockey Canada\'s rule: a suspected concussion means off {surface} right away and no return that day.',
            'Watch the next 24 to 48 hours: headache, sick to the stomach, foggy, off balance, light or noise bothering them, more emotional, sleeping differently. Symptoms can arrive hours later.',
            'See a doctor or nurse practitioner, and bring the Medical Assessment Letter back for the coach.'
        ],
        avoid: [
            '"You look fine." They may be, and the brain does not show.',
            'Letting them decide, because they want to play. The ladder decides: one step a day, school before {sport}.',
            'Screens, homework and video games as rest. Thinking is work for a healing brain.'
        ],
        ask: 'On a scale of 0 to 10, how is your head right now?',
        more: 'Neck pain, double vision, weakness or tingling, a headache getting worse, repeated vomiting, a seizure, passing out, or growing confusion: emergency, now. The Concussions page under Player Safety has the steps back.'
    },
    {
        id: 'hockey-at-home',
        title: 'When {sport} is taking over at home',
        when: 'Every dinner is {sport}, siblings are tired of it, the mood of the house follows the scoreboard.',
        first: [
            'One {sport}-free meal a week. Nobody mentions it, including you.',
            'Ask about the rest of their life as often as the game: friends, school, what they are watching.',
            'Notice if your own mood tracks their results. They notice it first.'
        ],
        avoid: [
            'Making the drive, the money or the schedule their problem to be grateful for.',
            'Letting a sibling\'s thing get bumped for {sport} without acknowledging it.',
            'Treating a loss as a family event.'
        ],
        ask: 'What is something good this week that had nothing to do with {sport}?',
        more: 'If {sport} has become the only thing holding the family calendar together, that is a season to look at the calendar, not the kid.'
    }
];

// The same guides for a school division (sports.js `school`): the same ids, so
// the guardian check-in routes the same way, with the situations a guardian
// actually meets: a bad test, a morning refusal, being left out, phones at night.
export const TALK_GUIDES_SCHOOL = [
    {
        id: 'car-ride-home',
        title: 'The ride home after a hard day',
        when: 'A bad test, a fight with a friend, a teacher who was unfair, and you are both in the car.',
        first: [
            '"Glad to see you." Then nothing. Silence is fine.',
            'Food and a drink before any conversation. Nobody debriefs well hungry.',
            'If they bring it up, listen. If they don\'t, leave it. The debrief can wait until they ask for it.'
        ],
        avoid: [
            'Going straight to the mark, the homework or the plan.',
            '"If you had just..." Anything that starts that way.',
            'Fixing it for them before they have said what it was.'
        ],
        ask: 'What was one thing that was okay today?',
        more: 'If every ride home is tense for a few weeks, that is a pattern, not a day. Worth a calm conversation on a weekend.'
    },
    {
        id: 'benched-or-cut',
        title: 'A bad mark, or their confidence dipped',
        when: 'A test that went badly, a report card, not making a team or a part, or a stretch where they have stopped believing in themselves.',
        first: [
            'Let them feel it first. "That is a hard one" beats a plan.',
            'Remind them of the evidence: a thing you have watched them get better at this year.',
            'When they are ready, ask what they would want to work on, and let them lead.'
        ],
        avoid: [
            'Emailing the teacher that night. Sleep on it, and if you still want to talk, do it privately and calmly.',
            'Comparing them with a sibling or a friend. Comparison does not build confidence; evidence does.',
            'Making the mark the only thing you ask about for a week.'
        ],
        ask: 'What is one thing you can do now that you could not in September?',
        more: 'If the dip lasts and starts showing up with friends or at home, the "gone quiet" guide applies.'
    },
    {
        id: 'before-tryouts',
        title: 'Before an exam or a big day',
        when: 'The week before exams, the night before a presentation, the morning of tryouts.',
        first: [
            'Nerves mean it matters. Say that out loud: "Nervous means you are ready."',
            'Routine over pep talk: same breakfast, bag packed the night before, phone out of the bedroom.',
            'Tell them you are proud whatever happens, before the result, not after.'
        ],
        avoid: [
            'Adding your own nerves to theirs.',
            'Last-minute quizzing at the door.',
            'Making the outcome the thing the whole family is waiting on.'
        ],
        ask: 'What would make tomorrow morning easier?',
        more: 'If the nerves stop them sleeping or eating for more than a few days, that is worth a word with the school counsellor.'
    },
    {
        id: 'gone-quiet',
        title: 'They have gone quiet',
        when: 'Quieter than usual, not joking around, in their room more, short answers for more than a week or two.',
        first: [
            'Sit beside them, not across from them. Car, kitchen, a walk.',
            '"I\'ve noticed you seem quieter. I\'m here when you want to talk." Then let it be.',
            'Keep the ordinary things going: meals together, the same routines.'
        ],
        avoid: [
            '"What\'s wrong with you?" Any version of it.',
            'Taking the phone as a first move. It can be the one place they feel okay.',
            'Asking five times a day. Once, warmly, and then being around.'
        ],
        ask: 'Is it school, friends, or something else? You don\'t have to tell me which.',
        more: 'Two weeks of this, or any talk of not wanting to be here, is the moment for the school counsellor or Kids Help Phone (1-800-668-6868), and for you to call too.'
    },
    {
        id: 'wants-to-quit',
        title: 'They don\'t want to go to school',
        when: '"I\'m not going." Mornings that start with a stomach ache, or a refusal out of nowhere.',
        first: [
            'Get curious before you get firm. Something is usually behind it: a person, a class, a fear.',
            '"What is the hardest part of the day?" is a better opener than "Why?"',
            'Agree one small step for tomorrow, not the whole week.'
        ],
        avoid: [
            'A standoff at the door every morning.',
            'Promising it will all be fine without knowing what it is.',
            'Letting it ride for weeks because each day was easier than the fight.'
        ],
        ask: 'If one thing about school changed, what would it be?',
        more: 'If it is more than a few days, loop in the school: a counsellor or a trusted teacher can change the day without anyone being singled out.'
    },
    {
        id: 'left-out',
        title: 'Friends, and being left out',
        when: 'A group that moved on without them, a chat they were dropped from, lunch alone.',
        first: [
            'Take it as seriously as they do. Being left out is a real hurt at any age.',
            'Help them name one person who is steady: a friend, a cousin, a teammate.',
            'Build the week around something they are good at and like.'
        ],
        avoid: [
            '"You\'ll make new friends." True, and useless on the day.',
            'Calling the other parents without asking them first.',
            'Treating it as drama. To them it is the whole weather.'
        ],
        ask: 'Who do you feel most like yourself around right now?',
        more: 'If being left out comes with being targeted, online or at school, tell the school the same week; that is their job and they want to know.'
    },
    {
        id: 'says-something-hurts',
        title: 'They say something hurts',
        when: 'Headaches, stomach aches, "I feel sick" on school mornings, tired all the time.',
        first: [
            'Believe them first. Pain is real whether or not there is a cause you can see.',
            'Notice the pattern: which days, which classes, after what.',
            'Sleep, food and water before anything else; most of it starts there.'
        ],
        avoid: [
            '"It\'s just nerves." Even when it is, it is not just.',
            'Keeping them home as a default; the days add up fast.',
            'Searching symptoms with them at 10 p.m.'
        ],
        ask: 'When did it start, and what was happening that week?',
        more: 'Pain that repeats on school days for a few weeks is worth a doctor\'s visit and a word with the school.'
    },
    {
        id: 'sleep-and-phones',
        title: 'Sleep, phones and mornings',
        when: 'Hard to wake, up late on a screen, running on empty by Wednesday.',
        first: [
            'Set the phone\'s bedtime, not just theirs: charging outside the bedroom is the single biggest change.',
            'Keep wake-up time steady, weekends included, within an hour.',
            'Make breakfast the easy default: something they will actually eat.'
        ],
        avoid: [
            'Negotiating the phone every single night. Decide once, together, then hold it.',
            'Catch-up sleep until noon on Saturday; it moves Monday further away.',
            'Caffeine after lunch, and energy drinks at all.'
        ],
        ask: 'What time do you think you actually fall asleep?',
        more: 'If they are sleeping nine hours and still exhausted, or not sleeping at all, that is a doctor conversation.'
    },
    {
        id: 'after-a-hit-to-the-head',
        title: 'After a hit to the head',
        when: 'A fall in gym, a collision at recess, a ball or a bike. They say they are fine, or they seem off.',
        first: [
            'Done with activity for the day, no exceptions. A suspected concussion means stop now and no going back that day.',
            'Watch the next 24 to 48 hours: headache, sick to the stomach, foggy, off balance, light or noise bothering them, more emotional, sleeping differently. Symptoms can arrive hours later.',
            'See a doctor or nurse practitioner, and tell the school what happened.'
        ],
        avoid: [
            '"You look fine." They may be, and the brain does not show.',
            'Letting them decide, because they do not want to miss something.',
            'Screens, homework and video games as rest. Thinking is work for a healing brain; school comes back in steps.'
        ],
        ask: 'On a scale of 0 to 10, how is your head right now?',
        more: 'Neck pain, double vision, weakness or tingling, a headache getting worse, repeated vomiting, a seizure, passing out, or growing confusion: emergency, now. Back to school in steps: a little, then part days, then full days.'
    },
    {
        id: 'hockey-at-home',
        title: 'School in a healthy place at home',
        when: 'School has become the only thing you talk about, or the thing you never talk about.',
        first: [
            'One school conversation a day, then other things.',
            'Ask about the parts that are not marks: friends, the funny thing, what they are reading.',
            'Let them hear you be proud of who they are, not what they got.'
        ],
        avoid: [
            'Making homework the family\'s evening.',
            'Letting a sibling\'s thing get bumped for a project without acknowledging it.',
            'Treating a report card as a family event.'
        ],
        ask: 'What is something good this week that had nothing to do with school?',
        more: 'If school has become the only thing holding the family calendar together, that is a season to look at the calendar, not the kid.'
    }
];

const guideSetFor = (sport) => (sport && sport.id === 'school' ? TALK_GUIDES_SCHOOL : TALK_GUIDES);

export const TALK_GUIDES_BY_ID = Object.fromEntries(TALK_GUIDES.map(g => [g.id, g]));
export const guideFor = (id, sport = null) => {
    const g = guideSetFor(sport).find(x => x.id === id) || null;
    return g && sport ? fillSportDeep(g, sport) : g;
};

// The guides with the org's sport words filled in (what the Parent Hub renders).
export const talkGuidesFor = (sport) => guideSetFor(sport).map(g => (sport ? fillSportDeep(g, sport) : g));

// The parent check-in items (ASSESSMENT_LIBRARY parent_v1), what each is called
// on the Parent Hub, and which guide a low answer opens.
export const PARENT_ITEMS = {
    parent_sleep:      { label: 'Wakes up ready to go',                   guide: 'sleep-and-phones' },
    parent_energy:     { label: 'Has the energy for school and {sport}',   guide: 'sleep-and-phones' },
    parent_fuel:       { label: 'Eats and drinks enough on {sport} days',  guide: 'sleep-and-phones' },
    parent_enjoyment:  { label: 'Enjoys {sport} right now',                guide: 'wants-to-quit' },
    parent_bounce:     { label: 'Bounces back after a bad game',          guide: 'car-ride-home' },
    parent_confidence: { label: 'Believes in themselves on {surface}',      guide: 'benched-or-cut' },
    parent_belonging:  { label: 'Feels part of the team',                 guide: 'left-out' },
    parent_pain:       { label: 'Tells me when something hurts',          guide: 'says-something-hurts' },
    parent_talks:      { label: 'Talks to me when something is wrong',    guide: 'gone-quiet' },
    parent_mood:       { label: 'Has seemed like themselves lately',      guide: 'gone-quiet' },
    parent_car_ride:   { label: 'The car ride home goes okay',            guide: 'car-ride-home' },
    parent_balance:    { label: '{Sport} stays in a healthy place at home', guide: 'hockey-at-home' },
    parent_words:      { label: 'I know what to say when they are upset', guide: 'car-ride-home' }
};
// The same items as a guardian reads them on a school org (sports.js `school`).
export const PARENT_ITEMS_SCHOOL = {
    parent_sleep:      { label: 'Wakes up ready for the day',             guide: 'sleep-and-phones' },
    parent_energy:     { label: 'Has the energy for the school day',      guide: 'sleep-and-phones' },
    parent_fuel:       { label: 'Eats and drinks enough on school days',  guide: 'sleep-and-phones' },
    parent_enjoyment:  { label: 'Enjoys school right now',                guide: 'wants-to-quit' },
    parent_bounce:     { label: 'Bounces back after a hard day',          guide: 'car-ride-home' },
    parent_confidence: { label: 'Believes in themselves in class',        guide: 'benched-or-cut' },
    parent_belonging:  { label: 'Feels part of their class and friends',  guide: 'left-out' },
    parent_pain:       { label: 'Tells me when something hurts',          guide: 'says-something-hurts' },
    parent_talks:      { label: 'Talks to me when something is wrong',    guide: 'gone-quiet' },
    parent_mood:       { label: 'Has seemed like themselves lately',      guide: 'gone-quiet' },
    parent_car_ride:   { label: 'The ride home goes okay',                guide: 'car-ride-home' },
    parent_balance:    { label: 'School stays in a healthy place at home', guide: 'hockey-at-home' },
    parent_words:      { label: 'I know what to say when they are upset', guide: 'car-ride-home' }
};
export const parentItemsFor = (sport) => (sport && sport.id === 'school' ? PARENT_ITEMS_SCHOOL : PARENT_ITEMS);
export const PARENT_METRIC_KEYS = Object.keys(PARENT_ITEMS);

// A parent's latest answers, sorted into what is worth a conversation (under
// 50), what to keep an eye on (50-74) and what is going well (75+). Each entry
// carries its guide so the hub can open it in one tap. Nothing here is a score.
export const parentRead = (stats, sport = null) => {
    const out = { watch: [], steady: [], good: [], answered: 0 };
    if (!stats || typeof stats !== 'object') return out;
    Object.entries(parentItemsFor(sport)).forEach(([key, item]) => {
        const v = stats[key];
        if (typeof v !== 'number' || !isFinite(v)) return;
        out.answered++;
        const entry = { key, label: sport ? fillSport(item.label, sport) : item.label, value: Math.round(v), guide: guideFor(item.guide, sport) };
        if (v < 50) out.watch.push(entry);
        else if (v < 75) out.steady.push(entry);
        else out.good.push(entry);
    });
    out.watch.sort((a, b) => a.value - b.value);
    return out;
};
